import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { INTERN_ROLES, daysUntil, ROLE_ACCENT } from '@/lib/constants';
import { AlertTriangle } from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Sidebar } from './Sidebar';
import { Button } from '@/components/ui/button';
import { Menu } from 'lucide-react';

export function AppShell({ children, title, actions }) {
  const { user } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);

  const days = user?.expires_at ? daysUntil(user.expires_at) : null;
  const showBanner = user && INTERN_ROLES.has(user.role) && days !== null && days <= 7;
  const isCritical = showBanner && days <= 2;

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />

      {/* Mobile drawer for sidebar */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="left" className="p-0 w-72">
          <div className="md:hidden">
            <Sidebar />
          </div>
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 border-b bg-card/60 backdrop-blur px-4 sm:px-6 flex items-center justify-between" data-testid="topbar">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setSheetOpen(true)} aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-display text-lg font-semibold leading-tight">{title}</h1>
              <div className="text-xs text-muted-foreground">Welcome back, {user?.name?.split(' ')[0]}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">{actions}</div>
        </header>

        {showBanner ? (
          <div
            className="px-4 sm:px-6 py-2 border-b flex items-center gap-2 text-sm"
            style={{
              backgroundColor: isCritical ? 'hsl(var(--destructive) / 0.08)' : 'hsl(28 78% 52% / 0.08)',
              color: isCritical ? 'hsl(var(--destructive))' : 'hsl(28 78% 40%)',
            }}
            data-testid="intern-expiry-banner"
          >
            <AlertTriangle className="h-4 w-4" />
            <span>
              Your account access expires in <strong>{days} day{days === 1 ? '' : 's'}</strong>. Contact HR to extend.
            </span>
          </div>
        ) : null}

        <main className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-6">{children}</main>
      </div>
    </div>
  );
}
